import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";
import { Loader2, Check, ExternalLink, CreditCard } from "lucide-react";
import { logError } from "../lib/log";

const FMT = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const STATUS_BADGE = {
  active: { label: "Active", className: "bg-emerald-50 text-emerald-700" },
  trialing: { label: "Trial", className: "bg-blue-50 text-blue-700" },
  past_due: { label: "Past due", className: "bg-rose-50 text-rose-700" },
  canceled: { label: "Canceled", className: "bg-stone-100 text-stone-700" },
  incomplete: { label: "Incomplete", className: "bg-amber-50 text-amber-700" },
};

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [p, s] = await Promise.all([
          axios.get(`${API_BASE}/billing/plans`),
          axios.get(`${API_BASE}/billing/subscription`),
        ]);
        if (cancel) return;
        setPlans(p.data.plans || []);
        setSub(s.data || null);
      } catch (e) {
        if (!cancel) toast.error(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    // Refresh subscription state if we just came back from Stripe checkout
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded")) {
      toast.success(`Subscription updated! Refreshing details...`);
      // Fire a server-side sync first (works even if webhooks aren't set up yet),
      // then re-fetch the subscription state.
      (async () => {
        try { await axios.post(`${API_BASE}/billing/sync`); }
        catch (e) { logError("billing:sync-after-checkout", e); /* best-effort */ }
        try {
          const r = await axios.get(`${API_BASE}/billing/subscription`);
          if (!cancel) setSub(r.data);
        } catch (e) { logError("billing:refresh-after-checkout", e); /* keep prior state */ }
      })();
    } else if (params.get("canceled")) {
      toast.info("Checkout canceled — no changes made");
    }
    return () => { cancel = true; };
  }, []);

  const upgrade = async (tier) => {
    setBusy(tier);
    try {
      const r = await axios.post(`${API_BASE}/billing/checkout-session`, {
        tier, origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await axios.post(`${API_BASE}/billing/portal-session`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  const currentTier = sub?.plan_tier;
  const badge = STATUS_BADGE[sub?.status] || null;

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="billing-page">
      <div className="mb-10">
        <div className="overline mb-2">Settings · Billing</div>
        <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Billing & plans</h1>
        <p className="text-[var(--ink-2)] mt-2">Subscriptions are processed securely by Stripe.</p>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div> : (
        <>
          {/* Current state */}
          <div className="card p-6 mb-10" data-testid="billing-current">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="overline mb-2">Current plan</div>
                <div className="flex items-baseline gap-3">
                  <span className="font-heading text-3xl" style={{ fontWeight: 500 }}>
                    {currentTier ? plans.find((p) => p.tier === currentTier)?.name || currentTier : "Free"}
                  </span>
                  {badge && (
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${badge.className}`}
                          data-testid="billing-status-badge">
                      {badge.label}
                    </span>
                  )}
                </div>
                {sub?.current_period_end && (
                  <div className="text-[13px] text-[var(--ink-3)] mt-2">
                    {sub.cancel_at_period_end
                      ? <>Cancels on <b>{fmtDate(sub.current_period_end)}</b></>
                      : <>Next renewal: <b>{fmtDate(sub.current_period_end)}</b></>}
                  </div>
                )}
                {sub?.trial_end && sub?.status === "trialing" && (
                  <div className="text-[13px] text-blue-700 mt-1">
                    Trial ends {fmtDate(sub.trial_end)}
                  </div>
                )}
              </div>
              {sub?.stripe_customer_id && (
                <button onClick={openPortal} disabled={busy === "portal"} className="btn-secondary"
                        data-testid="billing-portal-btn">
                  {busy === "portal" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                  Manage subscription
                </button>
              )}
            </div>
          </div>

          {/* Plan picker */}
          <div className="overline mb-3">{currentTier ? "Change plan" : "Pick a plan"}</div>
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((p) => {
              const isCurrent = p.tier === currentTier;
              return (
                <div key={p.tier}
                     className={`card p-6 flex flex-col ${isCurrent ? "border-emerald-300 bg-emerald-50/30" : ""}`}
                     data-testid={`billing-plan-${p.tier}`}>
                  <div className="font-heading text-2xl mb-1" style={{ fontWeight: 500 }}>{p.name}</div>
                  <div className="text-[13px] text-[var(--ink-3)] mb-4 min-h-[40px]">{p.blurb}</div>
                  <div className="mb-5">
                    <span className="font-mono text-3xl">{FMT.format(p.price_cents / 100)}</span>
                    <span className="text-[13px] text-[var(--ink-3)]"> / {p.interval}</span>
                  </div>
                  {p.trial_days > 0 && (
                    <div className="text-[12px] text-blue-700 mb-4">{p.trial_days}-day free trial</div>
                  )}
                  {isCurrent ? (
                    <button className="btn-secondary justify-center" disabled>
                      <Check size={14} /> Your current plan
                    </button>
                  ) : (
                    <button onClick={() => upgrade(p.tier)} disabled={!!busy} className="btn-primary justify-center"
                            data-testid={`billing-upgrade-${p.tier}`}>
                      {busy === p.tier ? <Loader2 size={14} className="animate-spin" /> : "Upgrade"}
                      {!busy && <ExternalLink size={12} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-[12px] text-[var(--ink-3)] mt-8">
            Cancel anytime from the "Manage subscription" portal. All payments processed by Stripe.
            Test cards: <code className="kbd text-[11px]">4242 4242 4242 4242</code> with any future expiry + any 3-digit CVC.
          </div>
        </>
      )}
    </div>
  );
}
